import { APP_CONFIG } from "@/config/app-config";
import { TaggedAuthRefreshTokenError } from "@/domain/errors/auth";
import { AuthTokens } from "@/domain/value-objects/auth";
import { AuthStorageRepository } from "@/infrastructure/repositories/storage/user-storage.repository";
import { isErr, safeTry } from "@/lib/result-pattern";
import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

// Helper functions
const createAuthHeader = (token: string) => `Token ${token}`;
const handleInvalidTokens = ({ soft = false }: { soft?: boolean }) => {
  if (soft) return;
  AuthStorageRepository.clearTokens();
  window.location.href = "/auth/login";
};
function handleInvalidSubscription() {
  window.location.href = "/users/subscription/plans";
}

const BaseAPIConfig = {
  baseURL: APP_CONFIG.getApiURL(),
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
};

const APIClient = axios.create(BaseAPIConfig);
const CleanAPIClient = axios.create(BaseAPIConfig);
const APIClientWithSoftAuth = axios.create(BaseAPIConfig);

const UniversalErrorHandler = (error: unknown) => Promise.reject(error);
const AuthHeaderMiddleware = () => (config: InternalAxiosRequestConfig) => {
  const accessToken = AuthStorageRepository.getAccessToken();
  if (accessToken && accessToken != "") {
    config.headers.Authorization = createAuthHeader(accessToken);
  }
  return config;
};

const UnauthenticatedErrorHandler =
  ({ client, soft }: { client: AxiosInstance; soft?: boolean }) =>
  async (error: AxiosError) => {
    if (error?.response?.status !== 401) {
      return Promise.reject(error);
    }

    const refreshToken = AuthStorageRepository.getRefreshToken();
    const accessToken = AuthStorageRepository.getAccessToken();
    if (!refreshToken || !accessToken) {
      handleInvalidTokens({ soft });
    }

    if (!refreshToken) {
      return Promise.reject(error);
    }

    // We need to use a new axios instance to avoid infinite loops in the interceptors
    const promise = axios.post<AuthTokens>(
      `${APP_CONFIG.getApiURL()}/auth/refresh/`,
      {
        refresh_token: refreshToken,
      }
    );

    const revalidationResult = await safeTry(
      async () => {
        return await promise;
      },
      () => new TaggedAuthRefreshTokenError({ mustInvalidateSession: true })
    );

    if (isErr(revalidationResult)) {
      if (revalidationResult.error.mustInvalidateSession) {
        handleInvalidTokens({ soft });
      }
      return Promise.reject(revalidationResult.error);
    }

    const response = revalidationResult.value;

    AuthStorageRepository.setAccessToken(response.data.access_token);
    AuthStorageRepository.setRefreshToken(response.data.refresh_token);
    if (!error?.config) {
      // If there's no config, we can't retry the request
      return Promise.reject(error);
    }
    error.config.headers["Authorization"] = createAuthHeader(
      response.data.access_token
    );

    // Retry the original request with the new access token
    return client(error.config);
  };

const SubscriptionErrorHandler = () => async (error: AxiosError) => {
  if (error?.response?.status !== 402) {
    return Promise.reject(error);
  }

  handleInvalidSubscription();
  return Promise.reject(error);
};

// Add authorization header to all requests
APIClient.interceptors.request.use(
  AuthHeaderMiddleware(),
  UniversalErrorHandler
);
APIClientWithSoftAuth.interceptors.request.use(
  AuthHeaderMiddleware(),
  UniversalErrorHandler
);

// Handle subscription errors
APIClient.interceptors.response.use(
  (response) => response,
  SubscriptionErrorHandler()
);

//Handle token expiration or invalid token
APIClient.interceptors.response.use(
  (response) => response,
  UnauthenticatedErrorHandler({ client: APIClient })
);

APIClientWithSoftAuth.interceptors.response.use(
  (response) => response,
  UnauthenticatedErrorHandler({ client: APIClientWithSoftAuth, soft: true })
);

export { CleanAPIClient, APIClient, APIClientWithSoftAuth };
export default APIClient;
